import React, { ChangeEventHandler, useRef, useState } from "react";
import { FieldTypes, PropertyConfig } from "../PropertiesPanel/types";
import { TimelineAudioState } from "../../contexts/PageAudioManager/types";
import { EInteractiveAudioType, LanguageType } from "../../models/IInteractiveAudio";
import { useAudioManagerStore } from "../../contexts/PageAudioManager";
import { PropertiesPanel } from "../PropertiesPanel/PropertiesPanel";
import { TextToSpeechModal } from "../../components/Modals/TextToSpeechModal";
import blobUrlFormatHelper from "../../components/blobUrlFormatHelper";
import { useAudioUpload } from "../../hooks/useAudioUpload";

import type { TextToSpeechModalProps } from "../../components/Modals/TextToSpeechModal/types";

import { renderTimeLength } from "./helpers";
import { AudioActions } from "./AudioActions";
import { useConfirmation } from "../../contexts/Confirmation";
import { getSecondary } from "../../contexts/PageAudioManager/helpers";
import { nanoid } from "../../lib/nanoId";
import { AlternativeNarrationModal } from "../../components/Modals/AlternativeNarrationModal/AlternativeNarrationModal";
import { useLessonPage } from "../../contexts/LessonPagesProvider/LessonPagesProvider";
import { useLessonSettingsState } from "../../components/LessonSettings/LessonSettingsContext/LessonSettingsProvider";
import { AudioOptionsPopover } from "../TimelinePanel/Right/CompositeNarrationRow/AudioOptionsPopover";
import { AudioPopoverAction } from "../TimelinePanel/Right/CompositeNarrationRow/types";
import { Whisper } from "rsuite";
import { audioBlobToApi } from "../TimelinePanel/helpers";

interface PageManifestWithNarration {
  narrationAudios?: Array<{ objectId: string; savedText?: string }>;
}

export const AudioPropertiesPanel = () => {
  const { pageManifest, updateManifest } = useLessonPage();

  const audioPanelStructure: PropertyConfig<TimelineAudioState> = [
    {
      identifier: "input",
      type: FieldTypes.AUDIO,
      label: "Audio File",
      readOnly: true,
    },
    {
      identifier: "title",
      type: FieldTypes.TEXT,
      label: "Object Name",
    },
    {
      identifier: "duration",
      type: FieldTypes.DISPLAY,
      label: "Audio Length",
      render: (audio) => {
        const length = audio.duration ?? audio.end - audio.start;

        return renderTimeLength(length);
      },
    },
    {
      identifier: "savedText",
      type: FieldTypes.TEXTAREA,
      className: "audio-narration-textarea",
      label: "Narration Text",
      visible: (audio) => {
        return audio.type === EInteractiveAudioType.NARRATION;
      },
      render: (audio) => {
        const manifestAudio = pageManifest?.narrationAudios?.find((a) => a.objectId === audio.objectId);
        const currentValue = manifestAudio?.savedText ?? audio.savedText ?? "";
        const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
          const newValue = e.target.value;
          if (manifestAudio) {
            manifestAudio.savedText = newValue;
          }
          updateManifest(pageManifest);
          updateAudio(audio.objectId, { savedText: newValue });
        };
        return <textarea className="audio-narration-textarea" value={currentValue} onChange={handleChange} />;
      },
    },
  ];

  const {
    lessonMetaDataLocalCopy: {
      additionalSettings: { studentLanguages },
    },
  } = useLessonSettingsState();

  const audioInputRef = useRef<HTMLInputElement>(null);
  const secondaryAudioInputRef = useRef<HTMLInputElement>(null);
  const secondaryPopoverRef = useRef<{ close: () => void } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSecondaryModalOpen, setIsSecondaryModalOpen] = useState(false);
  const [isAlternativeModalOpen, setIsAlternativeModalOpen] = useState(false);
  const [isSecondaryPopoverOpen, setIsSecondaryPopoverOpen] = useState(false);

  const [
    selectedAudio,
    selectAudio,
    removeAudio,
    replaceNarrationAudio,
    updateAudio,
    loadNarrationAudio,
    setIsDirty,
    removeEffectAudio,
    audioType,
  ] = useAudioManagerStore((state) => [
    state.getSelectedAudio(),
    state.selectAudio,
    state.removeAudio,
    state.replaceNarrationAudio,
    state.updateAudio,
    state.loadNarrationAudio,
    state.setIsDirty,
    state.removeEffectAudio,
    state.audioTypes,
  ]);

  const secondaryAudio = useAudioManagerStore((state) =>
    state.selectedAudio ? getSecondary(state.selectedAudio, state.narrationAudios) : null,
  );

  const { upload } = useAudioUpload();
  const { confirm } = useConfirmation();

  // Handlers
  const handleOnChange = (identifier: string, value: unknown) => {
    if (selectedAudio?.objectId) {
      updateAudio(selectedAudio.objectId, {
        [identifier]: value,
      });

      setIsDirty(true);
    }
  };

  const handleUpload = () => {
    audioInputRef?.current?.click();
  };

  const handleTextToSpeech = () => {
    setIsModalOpen(true);
  };

  const handleRemoveAudio = async () => {
    const shouldRemove = await confirm({
      title: "Remove Audio",
      message: "Are you sure you want to delete this audio from the timeline?",
    });

    if (!shouldRemove || !selectedAudio) {
      return;
    }

    const id = selectedAudio.objectId;
    const type = audioType.get(id);

    if (type === EInteractiveAudioType.NARRATION) {
      removeAudio(id);
    } else if (type === EInteractiveAudioType.EFFECT) {
      removeEffectAudio(id);
    }
    setIsDirty(true);
  };

  const handleAlternativeModalFinish = () => {
    setIsAlternativeModalOpen(false);
    // After setting the language, show the popover to choose TTS or upload
    setIsSecondaryPopoverOpen(true);
  };

  const handleSecondaryUpload = () => {
    secondaryAudioInputRef?.current?.click();
  };

  const handleSecondaryTextToSpeech = () => {
    setIsSecondaryModalOpen(true);
  };

  const handleSecondaryBrowseFiles: ChangeEventHandler<HTMLInputElement> = (event) => {
    const file = event.target.files?.[0];

    if (file && selectedAudio) {
      upload(file)
        .then(async (uploadedFile) => {
          if (uploadedFile) {
            await loadNarrationAudio(nanoid(), audioBlobToApi(uploadedFile.blobPath), {
              start: selectedAudio.start,
              type: EInteractiveAudioType.NARRATION,
              title: selectedAudio.title ?? `Audio ${nanoid()}`,
              savedText: (selectedAudio.savedText as string | undefined) ?? "",
              language: LanguageType.SECONDARY,
              parentObjectId: selectedAudio.objectId,
            });
            setIsDirty(true);
          }
        })
        .catch((error) => {
          console.error("Failed to upload secondary audio file:", error);
        });
    }
  };

  const handleSecondaryTTSAudio: TextToSpeechModalProps["onFinish"] = ({ generatedAudio, ...ttsProps }) => {
    setIsSecondaryModalOpen(false);

    if (!generatedAudio || !selectedAudio) {
      return;
    }

    loadNarrationAudio(generatedAudio.assetId.toString(), audioBlobToApi(generatedAudio.blobPath), {
      start: selectedAudio.start,
      type: EInteractiveAudioType.NARRATION,
      title: selectedAudio.title ?? `Audio ${generatedAudio.assetId}`,
      savedText: ttsProps.narrationText ?? selectedAudio.savedText ?? "",
      language: LanguageType.SECONDARY,
      parentObjectId: selectedAudio.objectId,
      ttsProps,
    })
      .then(() => {
        setIsDirty(true);
      })
      .catch((error) => {
        console.error("Failed to load secondary TTS audio:", error);
      });
  };

  const handleCreateSecondary = () => {
    const [, alternateLanguage] = studentLanguages;

    if (alternateLanguage) {
      // Alternate language is set, show the popover directly
      setIsSecondaryPopoverOpen(true);
    } else {
      // No alternate language set, show the alternative language modal first
      setIsAlternativeModalOpen(true);
    }
  };

  const handleSecondaryPopoverSelect = (action: AudioPopoverAction) => {
    secondaryPopoverRef?.current?.close();
    setIsSecondaryPopoverOpen(false);

    const [, alternateLanguage] = studentLanguages;

    if (alternateLanguage) {
      // Alternate language is set, directly show the TTS or upload modal
      if (action === AudioPopoverAction.TextToSpeech) {
        handleSecondaryTextToSpeech();
      } else {
        handleSecondaryUpload();
      }
    } else {
      // No alternate language set, show the alternative language modal
      setIsAlternativeModalOpen(true);
    }
  };

  const handleBrowseFiles: ChangeEventHandler<HTMLInputElement> = (event) => {
    const file = event.target.files?.[0];

    if (file && selectedAudio) {
      upload(file)
        .then(async (uploadedFile) => {
          if (uploadedFile) {
            await replaceNarrationAudio(selectedAudio.objectId, {
              objectId: uploadedFile.assetId.toString(),
              input: blobUrlFormatHelper(uploadedFile.blobPath),
              start: selectedAudio.start,
            });
            setIsDirty(true);
          }
        })
        .catch((error) => {
          console.error("Failed to upload audio file:", error);
        });
    }
  };

  const handleTTSAudio: TextToSpeechModalProps["onFinish"] = async ({ generatedAudio, ...ttsProps }) => {
    setIsModalOpen(false);

    if (!generatedAudio || !selectedAudio) {
      return;
    }

    await replaceNarrationAudio(selectedAudio.objectId, {
      objectId: generatedAudio.assetId.toString(),
      input: blobUrlFormatHelper(generatedAudio.blobPath),
      start: selectedAudio.start,
      ttsProps,
    });
    setIsDirty(true);
  };

  function handleChangeLanguage(language: LanguageType) {
    if (language === LanguageType.SECONDARY) {
      if (secondaryAudio) {
        selectAudio(secondaryAudio.objectId);
      }
      return;
    }

    const parentObjectId = selectedAudio?.parentObjectId;

    if (parentObjectId) {
      selectAudio(parentObjectId);
    }
  }

  // Extract TTS modal data computation - used in both branches
  const getTTSModalData = (): TextToSpeechModalProps["data"] => {
    const manifestAudio = pageManifest?.narrationAudios?.find((a) => a.objectId === selectedAudio?.objectId);
    return {
      ...(selectedAudio?.ttsProps || undefined),
      narrationText: manifestAudio?.savedText ?? selectedAudio?.savedText,
    };
  };

  const getSecondaryTTSModalData = (): TextToSpeechModalProps["data"] => {
    const manifestAudio = pageManifest?.narrationAudios?.find((a) => a.objectId === selectedAudio?.objectId);
    return {
      narrationText: manifestAudio?.savedText ?? selectedAudio?.savedText,
    };
  };

  // Extract common JSX elements
  const renderFileInput = () => (
    <input ref={audioInputRef} type="file" onChange={handleBrowseFiles} className="hidden-input" accept="audio/*" />
  );

  const renderSecondaryFileInput = () => (
    <input
      ref={secondaryAudioInputRef}
      type="file"
      onChange={handleSecondaryBrowseFiles}
      className="hidden-input"
      accept="audio/*"
    />
  );

  const renderTextToSpeechModal = () => (
    <TextToSpeechModal
      onFinish={handleTTSAudio}
      onClose={() => setIsModalOpen(false)}
      data={getTTSModalData()}
      open={isModalOpen}
    />
  );

  const renderSecondaryTextToSpeechModal = () => (
    <TextToSpeechModal
      onFinish={handleSecondaryTTSAudio}
      onClose={() => setIsSecondaryModalOpen(false)}
      data={getSecondaryTTSModalData()}
      open={isSecondaryModalOpen}
      isAlternate
    />
  );

  const renderAddButtonWrapper = (button: React.ReactNode) => (
    <Whisper
      controlId="secondary-audio-options"
      placement="bottom"
      trigger="none"
      open={isSecondaryPopoverOpen}
      ref={secondaryPopoverRef}
      speaker={<AudioOptionsPopover onSelect={handleSecondaryPopoverSelect} />}
    >
      {button}
    </Whisper>
  );

  if (selectedAudio) {
    const type = audioType.get(selectedAudio.objectId);
    return (
      <>
        <PropertiesPanel<TimelineAudioState>
          identifier={selectedAudio.objectId}
          data={selectedAudio}
          config={audioPanelStructure}
          onChange={handleOnChange}
          actionsSlot={
            <AudioActions
              type={type as EInteractiveAudioType}
              audio={selectedAudio}
              hasSecondaryAudio={!!secondaryAudio}
              onUpload={handleUpload}
              onTextToSpeech={handleTextToSpeech}
              onRemove={handleRemoveAudio}
              onChangeLanguage={handleChangeLanguage}
              onCreateSecondary={handleCreateSecondary}
              pageManifest={pageManifest}
              addButtonWrapper={renderAddButtonWrapper}
            />
          }
        />
        {renderFileInput()}
        {renderSecondaryFileInput()}
        <AlternativeNarrationModal
          open={isAlternativeModalOpen}
          onClose={() => setIsAlternativeModalOpen(false)}
          onFinish={handleAlternativeModalFinish}
        />
        {renderTextToSpeechModal()}
        {renderSecondaryTextToSpeechModal()}
      </>
    );
  }

  return <div>Select Audio</div>;
};
